import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import Detail from '../components/DesignerCommon/Detail'
import DesignGameCard from '../components/DesignGameCard'
import DesignFeedContainer from '../components/DesignerDesigns/DesignFeedContainer'
import Feed from '../components/DesignerFeed/Feed'
import { FEED_RESET } from '../redux/constants'
import { fetchFeed, setAttr } from '../redux/feedInfiniteSlice'
import { fetchDesignGames } from '../redux/designerReducers/designGamesReducer'
import { fetchChallenges } from '../redux/designerReducers/challengeReducer'
import { ReactComponent as BackIcon } from '../assets/backcircle.svg'
import { useIsMobile } from '../hooks/useIsMobile'
import useMediaQuery from '../hooks/useMediaQuery'

const CardWrapper = styled.div`
  border-radius: 10px;
  overflow: hidden;
  @media (max-width: 991px) {
    width: calc(50% - 5px);
    aspect-ratio: 1/1;

    & > * {
      width: 100%;
      height: 100%;
    }
  }
`

const Card = props => (
  <CardWrapper>
    <DesignGameCard wide {...props} />
  </CardWrapper>
)

const tabList = [
  {
    label: 'All',
    value: 'designGames'
  },
  {
    label: 'Games',
    value: 'challenge'
  },
  {
    label: 'Events',
    value: 'event'
  }
]

const Back = styled(BackIcon)`
  cursor: pointer;
`

const BackContainer = styled.div`
  position: absolute;
  top: 10px;

  @media only screen and (min-width: 768px) {
    left: 16px;
    top: 10px;
  }
`

const Container = styled.div`
  background: #f5f5f5;
  min-height: 100%;
  position: relative;
  padding-bottom: 100px;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  padding: 30px 0 20px;
  margin-bottom: 24px;

  position: sticky;
  top: 0;
  z-index: 5;

  @media only screen and (max-width: 768px) {
    padding: 15px 8px;
  }
`

const Title = styled.h1`
  font-family: Helvetica Neue;
  font-size: 28px;
  font-weight: 700;
  line-height: 34px;
  margin: 0 0 24px;

  @media only screen and (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 18px;
  }
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 20px;
`

const TabList = styled.div`
  display: flex;
  flex-direction: row;
  gap: 18px;
  flex-wrap: wrap;

  @media only screen and (max-width: 768px) {
    gap: 5px;
  }
`

const Tab = styled.button`
  height: 42px;
  width: 172px;
  border-radius: 6px;
  padding: 10px;
  font-family: Helvetica Neue;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0em;
  text-align: center;
  color: ${({ isActive }) => (isActive ? '#fff' : '#000')};
  background-color: ${({ isActive }) => (isActive ? '#FF5E6D' : '#e5e5e5')};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;

  @media only screen and (max-width: 768px) {
    width: fit-content;
    height: auto;
    padding: 6px 14px;
    font-size: 12px;
    line-height: 12px;
    border-radius: 3px;
    font-weight: 500;
    color: ${({ isActive }) => (isActive ? '#fff' : '#000')};
    background-color: ${({ isActive }) => (isActive ? '#FF5E6D' : '#F2F2F2')};
    border: 0.5px solid #d8d8d8;
  }
`

const HostButton = styled.button`
  height: 50px;
  width: 224px;
  border-radius: 80px;
  background-color: #ff5e6d;
  font-family: Helvetica Neue;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0em;
  text-align: left;
  color: #ffffff;
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  cursor: pointer;
  border: none;
  @media only screen and (max-width: 768px) {
    display: none;
  }
`

const HostMobileButton = styled(HostButton)`
  display: none;
  height: 32px;
  width: 116px;
  border-radius: 80px;
  font-size: 12px;
  line-height: 15px;
  padding: 0;
  align-self: baseline;

  @media only screen and (max-width: 768px) {
    display: flex;
  }
`

const Center = styled.div`
  width: 100%;
  margin: auto;

  @media only screen and (min-width: 768px) {
    width: 768px;
  }

  @media only screen and (min-width: 992px) {
    width: 950px;
  }

  @media only screen and (min-width: 1400px) {
    width: 1270px;
  }

  @media only screen and (max-width: 800px) {
    padding: 0 6px;
    box-sizing: border-box;
  }
`

const Games = props => {
  const dispatch = useDispatch()
  const router = useRouter()
  const [seconds, setSeconds] = useState(0)
  const [currentTab, setTab] = useState('designGames')

  const isMobile = useIsMobile()
  const isTablet = useMediaQuery('(max-width: 1024px)')

  const {
    challenge,
    loading: challengeLoading,
    challengeError,
    challengePageEnd
  } = useSelector(state => state.challenge)

  const {
    designGames,
    loading: designGamesLoading,
    designGamesError,
    designGamesPageEnd
  } = useSelector(state => state.designGames)

  const {
    feed: eventList,
    loading: eventLoading,
    error: eventError,
    end: eventEnd
  } = useSelector(state => state.myEventPlayTab)

  const tabsProps = {
    designGames: {
      data: designGames,
      loading: designGamesLoading,
      error: designGamesError,
      pageEnd: designGamesPageEnd
    },
    challenge: {
      data: challenge,
      loading: challengeLoading,
      error: challengeError,
      pageEnd: challengePageEnd
    },
    event: {
      data: eventList,
      loading: eventLoading,
      error: eventError,
      pageEnd: eventEnd
    }
  }

  const handleBack = () => {
    router.back()
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds(seconds => seconds + 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)

    if (currentTab === 'designGames') dispatch(fetchDesignGames())
    if (currentTab === 'challenge') dispatch(fetchChallenges())
    if (currentTab === 'event') {
      const attr = {
        addition: {
          statuses: ['active'],
          filter: ['active']
        }
      }

      dispatch(setAttr('myEventPlayTab')(attr))
      dispatch(fetchFeed('myEventPlayTab')({ status: FEED_RESET }))
    }
    setSeconds(0)
  }, [dispatch, currentTab])

  const fetchMore = () => {
    if (currentTab === 'designGames') dispatch(fetchDesignGames())
    if (currentTab === 'challenge') dispatch(fetchChallenges())
    if (currentTab === 'event') dispatch(fetchFeed('myEventPlayTab')())
  }

  return (
    <Container>
      <Header>
        {!isMobile && !isTablet && (
          <BackContainer>
            <Back onClick={handleBack} />
          </BackContainer>
        )}
        <Center>
          <Row>
            <Title>Play Design Games</Title>
            <HostMobileButton onClick={() => router.push('/create/userEvent')}>
              Host
            </HostMobileButton>
          </Row>
          <Row>
            <TabList>
              {tabList.map(tab => (
                <Tab
                  key={tab.value}
                  onClick={() => setTab(tab.value)}
                  isActive={tab.value === currentTab}
                >
                  {tab.label}
                </Tab>
              ))}
            </TabList>
            <HostButton onClick={() => router.push('/create/userEvent')}>
              Host MyEvent
            </HostButton>
          </Row>
        </Center>
      </Header>
      <Center>
        <DesignFeedContainer>
          <Feed
            {...tabsProps[currentTab]}
            end={true}
            tick={seconds}
            card={Card}
            from="/challenges"
            // onFetch={fetchMore}
          />
        </DesignFeedContainer>
      </Center>
    </Container>
  )
}

export default Games
